import { filesApi } from '@papan/files/client'
import { moveResultMessage, isMountId, type PartitionGate } from './protectedBranchFlow'
import { moveErrorText } from './moveErrorText'
import { captureUndoContext, type UndoCapture } from '@papan/core-ui/ui/undoCapture'
import { withMoveProgress, type MoveProgressToaster } from './moveProgress'
import { pinnedBatch, type PinnedBatch } from '@papan/api-client'

type T = (key: string, opts?: Record<string, unknown>) => string
type MoveResult = Awaited<ReturnType<typeof filesApi.moveFile>>

/**
 * Move each item into `targetId` and report what the SERVER did, as every move surface does
 * (App.tsx `handleDriveDrop`): a #801 split is named as a split (what moved, what stayed) and
 * recorded for undo — never as "Moved"; a #753 copy (an item out of a received `.shared`) is
 * "Copied" and never a move undo; a returned gesture the caller has no dialog for (an access
 * change, a chain escape, a reverse share) moved nothing. Mount (`s_`) ids are the SOURCE
 * tenant's, so they are never recorded for undo. With a `gate` (the move preview), each item
 * is previewed first: a stop skips it, a confirmed split carries the previewed set.
 * Used by the Library / My Space sidebar drop and the drive's cut-paste of mount items.
 *
 * #1316 review round 2 (owner, 2026-10-28): the moves FINISH as the identity that started them (`batch`, else one taken
 * here) — a tenant / identity switch mid-batch neither stops them nor sends the rest as the next session; nothing of
 * the batch (a toast, progress, an undo record) is shown once that session ended.
 */
export async function moveItemsInto(
  files: Array<{ id: string; name: string; isFolder: boolean }>,
  targetId: string | null,
  { t, toast, undo, gate, copy, batch: given }: {
    /** Copy → Paste of items out of a received `.shared`: COPY them (the source stays). Absent: a
     *  move — across organisations the source is removed in its owning tenant (by permission). */
    copy?: boolean
    t: T
    /** With `progress`, a big folder's move (a job, #1167 S4) shows "Moving …" while it runs. */
    toast: { success: (m: string) => void; info: (m: string) => void; error: (m: string) => void } & MoveProgressToaster
    undo: { recordMoveResult: (at: UndoCapture, fileId: string, originalParentId: string | null, name: string, result: MoveResult) => void }
    gate?: (item: { id: string; name: string; isFolder: boolean }) => Promise<PartitionGate>
    /** The caller's pinned batch, when the moves are part of a larger one (a paste). */
    batch?: PinnedBatch
  },
): Promise<void> {
  const batch = given ?? pinnedBatch()
  // D134: the batch's identity — every item's requests are sent as it. A batch whose session ended before this call has
  // no undo to record: a capture taken now would belong to the NEW session (#1316 round 3).
  const at = batch.ended() ? null : captureUndoContext()
  const moved: string[] = []
  const copied: string[] = []
  let firstError = ''
  for (const f of files) {
    let r: MoveResult
    try {
      // A COPY carries nothing away, so nothing is partitioned — only a move is previewed.
      const g = gate && !copy ? await gate(f) : null
      if (g && !g.go) continue // refused or cancelled — the gate already said so
      r = await withMoveProgress(toast, t('driveView.toast.moving', { name: f.name }), (hooks) => filesApi.moveFile(f.id, { parentId: targetId, ...(g?.confirmed ? { expectRemovable: g.expectRemovable } : {}), ...(copy ? { copy: true } : {}) }, { ...hooks, ...batch.opts() }), batch.ended)
    } catch (err) {
      firstError ||= `${f.name}: ${moveErrorText(err, t)}`
      continue
    }
    if (r.accessChange || r.needsConfirmation || r.chainEscape || r.crossTenant) {
      firstError ||= `${f.name}: ${t('driveView.toast.moveFailed')}`
      continue
    }
    if (r.copied) { copied.push(f.name); continue }
    // A drag carries no original parent, so only a split (undone under its root) is
    // recorded — and never a mount split (its ids are the source tenant's).
    if (at && (r.movedAcrossOrgs || (r.split && !isMountId(f.id)))) undo.recordMoveResult(at, f.id, null, f.name, r)
    const msg = moveResultMessage(t, r)
    if (msg) { if (!batch.ended()) toast.info(`${f.name}: ${msg}`) }
    else moved.push(f.name)
  }
  if (batch.ended()) return
  const say = (names: string[], many: string, one: string) => {
    if (names.length === 0) return
    toast.success(names.length > 1 ? t(many, { name: names[0], count: names.length - 1 }) : t(one, { name: names[0] }))
  }
  say(moved, 'driveView.toast.moved', 'driveView.toast.movedOne')
  say(copied, 'driveView.toast.copied', 'driveView.toast.copiedOne')
  if (firstError) toast.error(firstError)
}
